import React from 'react';
import { TechDashboardCaseQuickView } from './TechDashboardCaseQuickView';
import { getCurrentDeviceSlaAssignment } from '../utils/deviceSla';
import {
  getDeviceWarrantyChipLabel,
  getDeviceWarrantyChipStyle,
  getDeviceWarrantyFirstMonthChipStyle,
  getDeviceWarrantyStatus
} from '../utils/deviceWarranty';

const CASE_BRAND_TEXT_TINT = 'rgba(18, 63, 67, 0.08)';
const CASE_BRAND_MUTED = '#64716F';
const CASE_UNASSIGNED_SOFT = '#FDEDEC';
const CASE_UNASSIGNED_TEXT = '#A63B32';
const CASE_UNASSIGNED_BORDER = '#E7B9B4';

export const TechDashboardCasesList = React.memo(function TechDashboardCasesList(props: any) {
  const {
    styles,
    baseUrl,
    paginatedCases,
    filteredCaseCount,
    casesPage,
    casesPageCount,
    pageStart,
    pageEnd,
    onCasesPageChange,
    casesPageSize,
    onCasesPageSizeChange,
    selectedCase,
    assignmentPickerCaseId,
    assigningCaseId,
    assignmentError,
    caseCardRefs,
    canAssignCases,
    canUpdateCaseStatus,
    canCloseCases,
    canUpdateCasePriority,
    canEditCaseProblem,
    caseAssigneesLoading,
    caseAssigneesError,
    caseAssigneeOptions,
    selectedCaseQuotes,
    selectedCaseQuotesLoading,
    caseServiceReportPreviewLoadingId,
    openCaseServiceReportPreview,
    editingCaseId,
    savingProblemId,
    editProblemSummary,
    editProblemDetails,
    problemEditError,
    caseWorkflowSavingById,
    caseWorkflowClosingById,
    caseWorkflowResolvingById,
    caseWorkflowErrorById,
    TASK_GROUPS,
    TASK_GROUP_META,
    INSTALL_PHASES,
    MAINTENANCE_VARIANTS,
    MAINTENANCE_COLOR,
    MAINTENANCE_VARIANT_ROUTES,
    getTsfadStatusMeta,
    getActiveCaseTask,
    resolveTaskGroupCode,
    formatDateTime,
    formatTaskStatus,
    parseProblemDetails,
    buildCaseSummarySnippet,
    getCaseDisplayNumber,
    needsModelVerification,
    getPendingDeviceInfo,
    canVerifySubmittedDevices,
    verifySubmittedDevice,
    devices,
    getStatusStyle,
    formatCaseStatusLabel,
    getPriorityStyle,
    formatPriorityLabel,
    getCaseAttachmentCount,
    closeCaseQuickView,
    getCaseWorkflowDraft,
    isCaseWorkflowDirty,
    stageCaseWorkflowDraft,
    saveCaseWorkflow,
    closeCaseWorkflow,
    resolveCaseWorkflow,
    resetCaseWorkflowDraft,
    setAssignmentPickerCaseId,
    setAssignmentError,
    setSelectedCase,
    applyCaseQuickViewRefresh,
    loadCaseDetails,
    handleAssignTaskGroup,
    handleOpenInstallPhase,
    handleOpenDecommission,
    guardTaskWorkspaceOpen,
    navigate,
    startProblemEdit,
    cancelProblemEdit,
    updateProblemDetail,
    addProblemDetailLine,
    removeProblemDetailLine,
    setEditProblemSummary,
    saveProblemDetails,
    setCaseQuotePreview,
    setCaseQuotePreviewContext,
    getQuoteDecisionBadge,
    getQuotePartsBadge
  } = props;

  return (
              <div style={styles.caseList}>
                <div style={styles.caseListHeaderRow}>
                  <div style={styles.caseListHeaderBlock}>
                    <div style={styles.caseListHeader}>Cases</div>
                    <div style={styles.caseListMeta}>
                      {filteredCaseCount === 0
                        ? 'No matching cases'
                        : `Showing ${pageStart}-${pageEnd} of ${filteredCaseCount} matching cases`}
                    </div>
                  </div>
                  {filteredCaseCount > 0 && (
                    <div style={styles.casePaginationControls}>
                      <div style={styles.casePageSizeGroup}>
                        <label style={styles.casePageSizeLabel}>Per page</label>
                        <select
                          style={styles.casePageSizeSelect}
                          value={casesPageSize}
                          onChange={event => onCasesPageSizeChange(Number(event.target.value))}
                        >
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>
                      {casesPageCount > 1 && (
                        <>
                          <button
                            type="button"
                            style={{
                              ...styles.casePaginationButton,
                              ...(casesPage <= 1 ? styles.casePaginationButtonDisabled : {})
                            }}
                            onClick={() => onCasesPageChange(casesPage - 1)}
                            disabled={casesPage <= 1}
                          >
                            Previous
                          </button>
                          <div style={styles.casePaginationStatus}>
                            Page {casesPage} of {casesPageCount}
                          </div>
                          <button
                            type="button"
                            style={{
                              ...styles.casePaginationButton,
                              ...(casesPage >= casesPageCount ? styles.casePaginationButtonDisabled : {})
                            }}
                            onClick={() => onCasesPageChange(casesPage + 1)}
                            disabled={casesPage >= casesPageCount}
                          >
                            Next
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                {paginatedCases.length === 0 ? (
                  <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                    No cases found
                  </div>
                ) : (
                  paginatedCases.map((caseItem: any) => {
                    const analysisMeta = getTsfadStatusMeta(caseItem);
                    const badgeStyle =
                      analysisMeta.variant === 'accepted'
                        ? { ...styles.analysisBadge, ...styles.analysisBadgeAccepted }
                        : analysisMeta.variant === 'pending'
                        ? { ...styles.analysisBadge, ...styles.analysisBadgePending }
                        : { ...styles.analysisBadge, ...styles.analysisBadgeIdle };
                    const compactBadgeStyle: React.CSSProperties = {
                      ...badgeStyle,
                      padding: '0.08rem 0.45rem',
                      fontSize: '0.68rem'
                    };
                    const createdAtDate = caseItem.createdAt ? new Date(caseItem.createdAt) : null;
                    const openedDate = createdAtDate
                      ? createdAtDate.toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric'
                        })
                      : 'Unknown date';
                    const receivedDateLabel = createdAtDate
                      ? createdAtDate.toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })
                      : 'Unknown date';
                    const caseAgeDays =
                      createdAtDate && Number.isFinite(createdAtDate.getTime())
                        ? Math.max(0, Math.floor((Date.now() - createdAtDate.getTime()) / 86400000))
                        : null;
                    const receivedAgeSummary =
                      caseAgeDays !== null
                        ? `Received ${receivedDateLabel} | Age ${caseAgeDays} day${caseAgeDays === 1 ? '' : 's'}`
                        : `Received ${receivedDateLabel}`;
                    const isSelected = selectedCase?.id === caseItem.id;
                    const activeTask = caseItem.activeTask || getActiveCaseTask(caseItem.tasks);
                    const currentTask = caseItem.displayTask || activeTask || null;
                    const currentGroupCode = resolveTaskGroupCode(currentTask?.type?.code);
                    const taskTypeCodeRaw = currentTask?.type?.code
                      ? currentTask.type.code.trim().toUpperCase()
                      : '';
                    const casePath = caseItem.id ? encodeURIComponent(caseItem.id) : null;
                    const disableCaseNavigation = !casePath;
                    const activeMeta = currentTask
                      ? TASK_GROUP_META[currentGroupCode] ?? TASK_GROUP_META.UNASSIGNED
                      : TASK_GROUP_META.UNASSIGNED;
                    const activeTaskTypeMeta = taskTypeCodeRaw
                      ? TASK_GROUP_META[taskTypeCodeRaw]
                      : null;
                    const activeVariantLabel =
                      activeTaskTypeMeta &&
                      activeTaskTypeMeta.parentCode &&
                      activeTaskTypeMeta.parentCode !== activeTaskTypeMeta.code
                        ? activeTaskTypeMeta.label
                        : null;
                    const normalizedCaseStatus = String(caseItem.status || '').trim().toUpperCase();
                    const isResolvedCase = normalizedCaseStatus === 'RESOLVED';
                    const isClosedCase = normalizedCaseStatus === 'CLOSED';
                    const cardStyle: React.CSSProperties = {
                      ...styles.caseCard,
                      ...(isSelected ? styles.caseCardSelected : {})
                    };
                    const highlightColor = currentTask ? activeMeta.color : undefined;
                    const selectionShadow = isSelected ? 'inset 0 0 0 1px rgba(217, 154, 51, 0.22)' : null;
                    const showAssignmentOptions = canAssignCases && assignmentPickerCaseId === caseItem.id;
                    const isAssigning = assigningCaseId === caseItem.id;
                    const assignmentErrorMessage =
                      assignmentError?.caseId === caseItem.id ? assignmentError.message : null;
                    const repairDescriptionOverride =
                      currentGroupCode === 'REPAIR' && analysisMeta.detail ? analysisMeta.detail : null;
                    const assignmentDescription = currentTask
                      ? repairDescriptionOverride ??
                        activeTaskTypeMeta?.description ??
                        activeMeta.description
                      : 'Choose the right task group to unlock a guided workflow.';
                    const assignedAt = currentTask?.createdAt
                      ? formatDateTime(currentTask.createdAt)
                      : null;
                    const assignmentStatusParts = currentTask
                      ? [
                          activeVariantLabel ? `Mode: ${activeVariantLabel}` : null,
                          currentTask.stage?.label ? `Stage: ${currentTask.stage.label}` : null,
                          currentTask.status ? `Status: ${formatTaskStatus(currentTask.status)}` : null,
                          assignedAt ? `Assigned ${assignedAt}` : null
                        ]
                          .filter(Boolean)
                          .join(' | ')
                      : '';
                    const reviewLabel = analysisMeta.variant === 'none' ? 'Run TSFAD' : 'Review TSFAD';
                    const showTsfadBadge = currentGroupCode === 'REPAIR';
                    const problemSource =
                      (caseItem as any).problemDescription ?? (caseItem as any).problemDesc ?? '';
                    const problemParsed = parseProblemDetails(problemSource);
                    const problemSummary = buildCaseSummarySnippet(problemParsed.summary);
                    const customerLabel =
                      (caseItem.customer as any)?.companyName ??
                      (caseItem.customer as any)?.company ??
                      (caseItem.customer as any)?.email ??
                      'Customer';
                    const pendingDeviceInfo =
                      typeof getPendingDeviceInfo === 'function'
                        ? getPendingDeviceInfo(caseItem)
                        : null;
                    const deviceModelLabel =
                      String(caseItem.device?.modelNumber || pendingDeviceInfo?.modelNumber || '').trim() ||
                      'Pending device';
                    const deviceSerialLabel =
                      String(caseItem.device?.serialNumber || pendingDeviceInfo?.serialNumber || '').trim();
                    const deviceAssetLabel =
                      String((caseItem.device as any)?.assetNumber || pendingDeviceInfo?.assetNumber || '').trim();
                    const deviceIdentifierLabel = deviceAssetLabel
                      ? `Asset ${deviceAssetLabel}${deviceSerialLabel ? ` | Serial ${deviceSerialLabel}` : ''}`
                      : deviceSerialLabel || 'Submitted identifier pending verification';
                    const activeSlaAssignment = getCurrentDeviceSlaAssignment(
                      caseItem.device?.slaAssignments,
                      caseItem.openedAt || caseItem.createdAt || null
                    );
                    const activeWarrantyStatus = getDeviceWarrantyStatus(
                      caseItem.device,
                      caseItem.openedAt || caseItem.createdAt || null
                    );
                    const taskAssigneeNames = Array.isArray(currentTask?.assignees)
                      ? currentTask.assignees
                          .map((entry: any) => String(entry?.fullName || entry?.username || '').trim())
                          .filter(Boolean)
                      : [];
                    const taskAssigneeIds = Array.isArray(currentTask?.assigneeUserIds)
                      ? currentTask.assigneeUserIds
                          .map((value: any) => String(value || '').trim())
                          .filter(Boolean)
                      : [];
                    const resolvedAssigneeNames =
                      taskAssigneeNames.length > 0
                        ? taskAssigneeNames
                        : taskAssigneeIds
                            .map((assigneeId: string) => {
                              const match = caseAssigneeOptions.find(
                                (option: any) =>
                                  option.id === assigneeId || option.username === assigneeId
                              );
                              return String(match?.fullName || match?.username || '').trim();
                            })
                            .filter(Boolean);
                    const uniqueResolvedAssigneeNames = Array.from(new Set(resolvedAssigneeNames));
                    const assigneeLabel =
                      uniqueResolvedAssigneeNames.length > 0
                        ? uniqueResolvedAssigneeNames.join(', ')
                        : taskAssigneeIds.length > 0
                        ? taskAssigneeIds.length === 1
                          ? 'Assigned'
                          : `Assigned (${taskAssigneeIds.length})`
                        : 'Unassigned';
                    const isUnassigned = assigneeLabel === 'Unassigned';
                    const taskLabel = currentTask ? activeMeta.label : 'Unassigned';
                    const attachmentCount = getCaseAttachmentCount(caseItem);
                    const isEditingProblem = editingCaseId === caseItem.id;
                    const isSavingProblem = savingProblemId === caseItem.id;
                    const caseCardQuotes = isSelected ? selectedCaseQuotes : [];
                    const caseCardQuotesLoading = isSelected && selectedCaseQuotesLoading;
                    const hasPendingNewDeviceVerification = Boolean(pendingDeviceInfo);
                    const workflowDraft = getCaseWorkflowDraft(caseItem);
                    const workflowDirty = isCaseWorkflowDirty(caseItem);
                    const workflowSaving = Boolean(caseWorkflowSavingById[caseItem.id]);
                    const workflowClosing = Boolean(caseWorkflowClosingById?.[caseItem.id]);
                    const workflowResolving = Boolean(caseWorkflowResolvingById?.[caseItem.id]);
                    const workflowError = caseWorkflowErrorById[caseItem.id] || '';

                    const cardStatusStyle: React.CSSProperties = {
                      ...getStatusStyle(caseItem.status),
                      padding: isClosedCase ? '0.2rem 0.62rem' : '0.16rem 0.5rem',
                      fontSize: isClosedCase ? '0.72rem' : '0.68rem',
                      lineHeight: 1.2
                    };
                    const cardPriorityStyle: React.CSSProperties = {
                      ...getPriorityStyle(caseItem.priority),
                      padding: '0.16rem 0.45rem',
                      fontSize: '0.68rem',
                      lineHeight: 1.2,
                      marginLeft: 0
                    };
                    const cardTaskChipStyle: React.CSSProperties = {
                      ...styles.assignmentChip,
                      padding: '0.16rem 0.55rem',
                      fontSize: '0.68rem',
                      lineHeight: 1.2,
                      backgroundColor: currentTask ? activeMeta.color : CASE_BRAND_TEXT_TINT,
                      color: currentTask ? '#ffffff' : CASE_BRAND_MUTED
                    };
                    const cardAssigneeStyle: React.CSSProperties = isUnassigned
                      ? {
                          ...styles.caseCardAssigneeMain,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          alignSelf: 'flex-end',
                          maxWidth: '100%',
                          width: 'fit-content',
                          padding: '0.12rem 0.48rem',
                          borderRadius: '999px',
                          backgroundColor: CASE_UNASSIGNED_SOFT,
                          color: CASE_UNASSIGNED_TEXT,
                          border: `1px solid ${CASE_UNASSIGNED_BORDER}`,
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: 0
                        }
                      : styles.caseCardAssigneeMain;
                    const caseRowStyle: React.CSSProperties = isResolvedCase
                      ? {
                          ...styles.caseCardTriColumnRow,
                          opacity: isSelected ? 0.82 : 0.62
                        }
                      : styles.caseCardTriColumnRow;
                    if (isResolvedCase && !isSelected) {
                      cardStyle.backgroundColor = '#F7F4EE';
                    }
                    const effectiveHighlightColor = isResolvedCase ? undefined : highlightColor;
                    const effectiveHighlightShadow = effectiveHighlightColor
                      ? `inset 4px 0 0 0 ${effectiveHighlightColor}`
                      : null;
                    const effectiveCombinedShadow = [effectiveHighlightShadow, selectionShadow].filter(Boolean).join(', ');
                    cardStyle.boxShadow = effectiveCombinedShadow || undefined;

                    return (
                      <div
                        key={caseItem.id}
                        ref={node => {
                          caseCardRefs.current[caseItem.id] = node;
                        }}
                        style={cardStyle}
                        onClick={() => {
                          setAssignmentPickerCaseId(null);
                          setAssignmentError(null);
                          setSelectedCase(caseItem);
                        }}
                      >
                        <div style={caseRowStyle}>
                          <div style={styles.caseCardPrimaryBlock}>
                            <div style={styles.caseCardTitleRow}>
                              <span style={styles.caseNumber}>
                                Case #{getCaseDisplayNumber(caseItem)}
                              </span>
                              {showTsfadBadge && <span style={compactBadgeStyle}>{analysisMeta.label}</span>}
                              {needsModelVerification(caseItem) && (
                                <span style={styles.modelVerificationBadge}>
                                  {hasPendingNewDeviceVerification ? 'Device Verification' : 'Model Verification'}
                                </span>
                              )}
                            </div>
                            <div style={styles.caseCardHeadline}>{problemSummary}</div>
                            <div style={styles.caseCardAttachmentMetaLeft}>
                              {attachmentCount > 0 ? `Attachments: ${attachmentCount}` : 'No attachments'}
                            </div>
                          </div>

                          <div style={styles.caseCardSecondaryBlock}>
                            <div style={styles.caseCardEntityValue}>{customerLabel}</div>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.45rem',
                                flexWrap: 'wrap'
                              }}
                            >
                              <div style={styles.caseCardEntityValue}>{deviceModelLabel}</div>
                              {activeSlaAssignment ? (
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    borderRadius: '999px',
                                    padding: '0.16rem 0.5rem',
                                    fontSize: '0.68rem',
                                    fontWeight: 700,
                                    backgroundColor: '#E7F2EC',
                                    color: '#25624D',
                                    border: '1px solid #BDD4C7'
                                  }}
                                >
                                  SLA
                                </span>
                              ) : null}
                              {activeWarrantyStatus.active ? (
                                <span style={getDeviceWarrantyChipStyle(activeWarrantyStatus)}>
                                  {getDeviceWarrantyChipLabel(activeWarrantyStatus)}
                                </span>
                              ) : null}
                              {activeWarrantyStatus.isReplacementPeriod ? (
                                <span style={getDeviceWarrantyFirstMonthChipStyle()}>First month</span>
                              ) : null}
                            </div>
                            <div style={styles.caseCardEntityValueMuted}>{deviceIdentifierLabel}</div>
                          </div>

                      <div style={styles.caseCardTertiaryBlock}>
                            <div style={styles.caseCardStatusWrap}>
                              <span style={cardStatusStyle}>
                                {formatCaseStatusLabel ? formatCaseStatusLabel(caseItem.status) : caseItem.status}
                              </span>
                              <span style={cardPriorityStyle}>{formatPriorityLabel(caseItem.priority)}</span>
                              <span style={cardTaskChipStyle}>{taskLabel}</span>
                            </div>
                            <div style={cardAssigneeStyle}>{assigneeLabel}</div>
                            <div style={styles.caseCardDateMetaRight}>{receivedAgeSummary}</div>
                          </div>
                        </div>
                        {isSelected && (
                          <TechDashboardCaseQuickView
                            styles={styles}
                            baseUrl={baseUrl}
                            caseItem={caseItem}
                            openedDate={openedDate}
                            analysisMeta={analysisMeta}
                            badgeStyle={badgeStyle}
                            workflowDraft={workflowDraft}
                            workflowDirty={workflowDirty}
                            workflowSaving={workflowSaving}
                            workflowClosing={workflowClosing}
                            workflowResolving={workflowResolving}
                            workflowError={workflowError}
                            currentTask={currentTask}
                            caseAssigneesLoading={caseAssigneesLoading}
                            caseAssigneeOptions={caseAssigneeOptions}
                            caseAssigneesError={caseAssigneesError}
                            canUpdateCaseStatus={canUpdateCaseStatus}
                            canCloseCases={canCloseCases}
                            canUpdateCasePriority={canUpdateCasePriority}
                            canAssignCases={canAssignCases}
                            saveCaseWorkflow={saveCaseWorkflow}
                            closeCaseWorkflow={closeCaseWorkflow}
                            resolveCaseWorkflow={resolveCaseWorkflow}
                            resetCaseWorkflowDraft={resetCaseWorkflowDraft}
                            stageCaseWorkflowDraft={stageCaseWorkflowDraft}
                            getCaseDisplayNumber={getCaseDisplayNumber}
                            getPendingDeviceInfo={getPendingDeviceInfo}
                            canVerifySubmittedDevices={canVerifySubmittedDevices}
                            verifySubmittedDevice={verifySubmittedDevice}
                            devices={devices}
                            getStatusStyle={getStatusStyle}
                            formatCaseStatusLabel={formatCaseStatusLabel}
                            getPriorityStyle={getPriorityStyle}
                            formatTaskStatus={formatTaskStatus}
                            formatPriorityLabel={formatPriorityLabel}
                            caseCardQuotes={caseCardQuotes}
                            caseCardQuotesLoading={caseCardQuotesLoading}
                            setCaseQuotePreview={setCaseQuotePreview}
                            setCaseQuotePreviewContext={setCaseQuotePreviewContext}
                            caseServiceReports={caseItem.serviceReports || []}
                            caseSurveys={caseItem.caseSurveys || []}
                            caseServiceReportPreviewLoadingId={caseServiceReportPreviewLoadingId}
                            openCaseServiceReportPreview={openCaseServiceReportPreview}
                            getQuoteDecisionBadge={getQuoteDecisionBadge}
                            getQuotePartsBadge={getQuotePartsBadge}
                            isEditingProblem={isEditingProblem}
                            canEditCaseProblem={canEditCaseProblem}
                            startProblemEdit={startProblemEdit}
                            problemParsed={problemParsed}
                            editProblemSummary={editProblemSummary}
                            setEditProblemSummary={setEditProblemSummary}
                            isSavingProblem={isSavingProblem}
                            editProblemDetails={editProblemDetails}
                            updateProblemDetail={updateProblemDetail}
                            removeProblemDetailLine={removeProblemDetailLine}
                            addProblemDetailLine={addProblemDetailLine}
                            problemEditError={problemEditError}
                            saveProblemDetails={saveProblemDetails}
                            cancelProblemEdit={cancelProblemEdit}
                            assignmentDescription={assignmentDescription}
                            assignmentStatusParts={assignmentStatusParts}
                            activeMeta={activeMeta}
                            assignmentErrorMessage={assignmentErrorMessage}
                            showAssignmentOptions={showAssignmentOptions}
                            activeGroupCode={currentGroupCode}
                            INSTALL_PHASES={INSTALL_PHASES}
                            handleOpenInstallPhase={handleOpenInstallPhase}
                            MAINTENANCE_VARIANTS={MAINTENANCE_VARIANTS}
                            taskTypeCodeRaw={taskTypeCodeRaw}
                            MAINTENANCE_VARIANT_ROUTES={MAINTENANCE_VARIANT_ROUTES}
                            navigate={navigate}
                            handleAssignTaskGroup={handleAssignTaskGroup}
                            MAINTENANCE_COLOR={MAINTENANCE_COLOR}
                            casePath={casePath}
                            disableCaseNavigation={disableCaseNavigation}
                            reviewLabel={reviewLabel}
                            handleOpenDecommission={handleOpenDecommission}
                            guardTaskWorkspaceOpen={guardTaskWorkspaceOpen}
                            TASK_GROUPS={TASK_GROUPS}
                            isAssigning={isAssigning}
                            assigningCaseId={assigningCaseId}
                            setAssignmentPickerCaseId={setAssignmentPickerCaseId}
                            assignmentError={assignmentError}
                            setAssignmentError={setAssignmentError}
                            loadCaseDetails={loadCaseDetails}
                            applyCaseQuickViewRefresh={applyCaseQuickViewRefresh}
                            closeCaseQuickView={closeCaseQuickView}
                          />
                        )}
                      </div>
                    );
                  })
                )}
                {filteredCaseCount > 0 && casesPageCount > 1 && (
                  <div style={styles.casePaginationFooter}>
                    <div style={styles.caseListMeta}>
                      Showing {pageStart}-{pageEnd} of {filteredCaseCount} matching cases
                    </div>
                    <div style={styles.casePaginationControls}>
                      <div style={styles.casePageSizeGroup}>
                        <label style={styles.casePageSizeLabel}>Per page</label>
                        <select
                          style={styles.casePageSizeSelect}
                          value={casesPageSize}
                          onChange={event => onCasesPageSizeChange(Number(event.target.value))}
                        >
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>
                      <button
                        type="button"
                        style={{
                          ...styles.casePaginationButton,
                          ...(casesPage <= 1 ? styles.casePaginationButtonDisabled : {})
                        }}
                        onClick={() => onCasesPageChange(casesPage - 1)}
                        disabled={casesPage <= 1}
                      >
                        Previous
                      </button>
                      <div style={styles.casePaginationStatus}>
                        Page {casesPage} of {casesPageCount}
                      </div>
                      <button
                        type="button"
                        style={{
                          ...styles.casePaginationButton,
                          ...(casesPage >= casesPageCount ? styles.casePaginationButtonDisabled : {})
                        }}
                        onClick={() => onCasesPageChange(casesPage + 1)}
                        disabled={casesPage >= casesPageCount}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
  );
});

